<template>
  <div class="reset-password-wrapper">
    <component
      :is="useV2 ? 'auth-container-v2' : 'auth-container'"
      :hide-header="codeModalOpened"
      :form-name="
        !codeModalOpened ? $t('resetPasswordPage.changePassword') : ''
      "
      class="auth-reset-password"
      @submit="onFormSubmit"
    >
      <template slot="default">
        <template v-if="!codeModalOpened">
          <exc-info-block
            class="mb-20"
            :content="$t('registration.passwordRules')"
          />
          <exc-input
            v-model="$v.email.$model"
            :field="$v.email"
            :label="$t('resetPasswordPage.eMail')"
            :optional-errors="{
              required: $t('validators.required'),
              email: $t('validators.email'),
              maxLength: $t('validators.maxLength', { max: 255 }),
              ...mix_getServerError('email'),
            }"
            class="auth-reset-password__email"
            type="email"
            size="s"
            autocomplete="email"
            @input="mix_clearServerError('email')"
          />
          <password-input
            v-model="$v.password.$model"
            :label="$t('resetPasswordPage.password')"
            :field="$v.password"
            size="s"
            :optional-errors="{
              required: $t('validators.required'),
              atLeastOneLowercase: $t('validators.atLeastOneLowercase'),
              atLeastOneUppercase: $t('validators.atLeastOneUppercase'),
              atLeastOneNumeric: $t('validators.atLeastOneNumeric'),
              atLeastOneSpecial: $t('validators.atLeastOneSpecial'),
              onlyLatinLettersOrNumbersAndChars: $t(
                'validators.onlyLatinLettersOrNumbersAndChars',
              ),
              minLength: $t('validators.minLength', { min: 8 }),
              maxLength: $t('validators.maxLength', { max: 20 }),
              ...mix_getServerError('password'),
            }"
            class="auth-reset-password__password"
            autocomplete="current-password"
            @input="mix_clearServerError('password')"
          />
          <password-input
            v-model="$v.password_confirmation.$model"
            :label="$t('resetPasswordPage.confirmation')"
            :field="$v.password_confirmation"
            :optional-errors="{
              sameAs: $t('validators.sameAs', {
                label: $t('auth.welcomePass'),
              }),
              ...mix_getServerError('password_confirmation'),
            }"
            size="s"
            class="auth-reset-password__password"
            autocomplete="new-password"
            @input="mix_clearServerError('password_confirmation')"
          />

          <generate-password @generate="generatePassword" />
          <!-- <p
            v-if="mix_getServerError('form').fromServer"
            class="txt txt--red txt--12px"
          >
            {{ mix_getServerError('form').fromServer }}
          </p> -->
        </template>
        <confirmation
          v-else
          :email="true"
          :email-code-resend-in="codeResendIn"
          :is-modal="false"
          :loading="codeModalLoading"
          :errors="mix_serverErrors"
          :extended-data="resendData"
          :device-confirmation-title="$t('auth.emailConfirmationTitle')"
          :device-confirmation-text="$t('auth.emailConfirmationText')"
          :back-btn-text="$t('auth.back')"
          device-confirmation-field="reset_confirmation_code"
          @input="() => mix_clearServerError('reset_confirmation_code')"
          @inserted="onInserted"
          @resend="onResend"
          @close="onCloseModal"
          @back="onCloseModal"
        />
      </template>
      <template v-if="!codeModalOpened" slot="btn">
        <exc-button
          v-if="!codeModalOpened"
          size="s"
          :loading="loading"
          full-width
          :disabled="$v.$invalid"
        >
          <span>
            {{ $t('resetPasswordPage.changePassword') }}
          </span>
        </exc-button>
      </template>
    </component>
  </div>
</template>

<script>
import { serverFormMixin } from '@/mixins/serverFormMixin'
// import { passwordValidations, onlyLatinLettersOrNumbersAndChars } from '@/plugins/validators'
import MixinNavLangSwitch from '@/mixins/MixinNavLangSwitch'
import AuthFormSubmit from '@/mixins/auth/AuthFormSubmit'
import AuthMixinCaptcha from '@/mixins/auth/AuthMixinCaptcha'
import { AUTH_CONTAINER_V2 } from '@/constants'
import { ExcButton, ExcInput, TdxInput } from '@/components/common'
import { validationMixin } from 'vuelidate'
import { email, maxLength, required, sameAs } from 'vuelidate/lib/validators'
// import { notifyError } from '@/lib/bus'
import defaultHeadMeta from '@/mixins/defaultHeadMeta'

const TEMP_ID_KEY = 'temp_user_id'

export default {
  name: 'AuthResetPassword',
  components: {
    ExcInput,
    ExcButton,
    TdxInput,
    ExcInfoBlock: () => import('@/components/common/blocks/ExcInfoBlock.vue'),
    AuthContainer: () =>
      import(
        /* webpackMode: "eager" */ '~/components/containers/AuthContainer.vue'
      ),
    AuthContainerV2: () =>
      import(
        /* webpackMode: "eager" */ '@/components/containers/AuthContainerV2.vue'
      ),
    GeneratePassword: () =>
      import(
        /* webpackMode: "eager" */ '@/components/button/GeneratePassword.vue'
      ),
    PasswordInput: () =>
      import(
        /* webpackMode: "eager" */ '@/components/ui-kit/text-input/PasswordInput.vue'
      ),
    PasswordInputWithKey: () =>
      import(
        /* webpackMode: "eager" */ '@/components/ui-kit/text-input/PasswordInputWithKey.vue'
      ),
    Confirmation: () =>
      import(
        /* webpackMode: "eager" */ '@/components/confirmation/Confirmation.vue'
      ),
  },
  mixins: [
    MixinNavLangSwitch,
    validationMixin,
    serverFormMixin,
    AuthMixinCaptcha,
    AuthFormSubmit,
    defaultHeadMeta,
  ],
  middleware: 'withoutAuthOnly',
  data () {
    return {
      useV2: AUTH_CONTAINER_V2,

      title: this.$t('resetPasswordPage.changePassword'),
      loading: false,

      email: '',
      password: '',
      password_confirmation: '',
      tempUserId: null,

      codeModalLoading: false,
      codeModalOpened: false,
      codeResendIn: null,
    }
  },
  computed: {
    isMobile () {
      if (!process.client) return
      return this.$mq.resize && this.$mq.below(991)
    },
    resendData () {
      return {
        email: this.email,
        [TEMP_ID_KEY]: this.tempUserId,
      }
    },
  },

  methods: {
    async submit () {
      this.loading = true

      try {
        const { response } = await this.$api.postPasswordCodeSend({
          email: this.email,
          ...this.mix_captchaData,
        })

        this.tempUserId = response.temp_user_id
        this.codeResendIn = parseFloat(response.life_time)
        this.codeModalOpened = true
      } catch ({ response }) {
        const errors = Object.values(response?.data.errors)

        if (errors) {
          this.mix_setServerErrors(errors)
          this.mix_notifyServerErrors(errors)
          return
        }
      } finally {
        this.loading = false
      }
    },

    async onInserted (data) {
      this.codeModalLoading = true
      try {
        await this.$api.postPasswordCodeValidate({
          code: data.email,
          email: this.email,
          password: this.password,
          temp_user_id: this.tempUserId,
        })
        this.localePush('/login')
        this.onCloseModal()
      } catch ({ response }) {
        if (response?.data?.errors) {
          this.mix_setServerErrors({
            reset_confirmation_code:
              response.data.errors.form || response.data.errors.message,
          })
        }
      } finally {
        this.codeModalLoading = false
      }
    },
    async onResend () {
      this.codeModalLoading = true
      try {
        await this.$api.postPasswordCodeResend({
          email: this.email,
          temp_user_id: this.tempUserId,
          code: this.codeResendIn,
        })
      } catch ({ response }) {
        if (response?.data?.errors?.form) {
          this.mix_setServerErrors({
            reset_confirmation_code: response.data.errors.form,
          })
        }
      } finally {
        this.codeModalLoading = false
      }
    },
    onCloseModal () {
      this.codeModalOpened = false
      this.codeResendIn = null
      this.tempUserId = null
    },
    generatePassword (generatedPass) {
      this.password = generatedPass
    },
  },

  validations () {
    return this.mix_validationsServerWrapper({
      email: {
        required,
        email,
        maxLength: maxLength(255),
      },
      password: {
        onlyLatinLettersOrNumbersAndChars: this.$validators
          .onlyLatinLettersOrNumbersAndChars,
        ...this.$validators.passwordValidations,
      },
      password_confirmation: {
        sameAs: sameAs('password'),
      },
    })
  },
}
</script>

<style lang="scss" scoped>
.reset-password-wrapper {
  display: flex;
  flex: 1;

  &::v-deep {
    .auth-reset-password__password:last-child {
      .exc-input-label-error-wrapper__content {
        margin: 0;
      }
    }
  }
}
</style>
